import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Typography, Row, Col, Select, DatePicker, Button,
  message, Drawer, Descriptions, Space
} from 'antd';
import {
  HistoryOutlined, ReloadOutlined, EyeOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import superAdminApi, { AuditLog, PaginationParams } from '../../api/superAdminApi';
import { useIsMobile } from '../../../../utils/responsiveUtils';
import MobileDataList from '../../../../components/mobile/MobileDataList';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const AuditLogs: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filterAction, setFilterAction] = useState<string | undefined>();
  const [filterTargetType, setFilterTargetType] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[any, any] | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<(AuditLog & { targetDetails?: any }) | null>(null);

  const fetchLogs = useCallback(async (params: PaginationParams = {}) => {
    try {
      setLoading(true);
      const response = await superAdminApi.getAuditLogs({
        page: params.page || pagination.page,
        limit: params.limit || pagination.limit,
        action: filterAction,
        targetType: filterTargetType,
        startDate: dateRange?.[0]?.toISOString(),
        endDate: dateRange?.[1]?.toISOString(),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (response.success) {
        setLogs(response.data);
        setPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total
        });
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterTargetType, dateRange, pagination.page, pagination.limit]);

  const fetchActionTypes = async () => {
    try {
      const response = await superAdminApi.getActionTypes();
      if (response.success) {
        setActionTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch action types', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchActionTypes();
  }, []);

  const handleTableChange = (paginationConfig: any) => {
    fetchLogs({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize
    });
  };

  const handleViewDetails = async (record: AuditLog) => {
    try {
      const response = await superAdminApi.getAuditLogById(record.id);
      if (response.success) {
        setSelectedLog(response.data);
        setDrawerVisible(true);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to load details');
    }
  };

  const getActionColor = (action: string): string => {
    if (action.includes('CREATE')) return 'green';
    if (action.includes('UPDATE') || action.includes('OVERRIDE')) return 'blue';
    if (action.includes('DELETE') || action.includes('SUSPEND')) return 'red';
    if (action.includes('ACTIVATE') || action.includes('REACTIVATE')) return 'cyan';
    if (action.includes('RESET')) return 'orange';
    if (action.includes('REFUND')) return 'purple';
    return 'default';
  };

  const getTargetTypeColor = (type: string): string => {
    switch (type) {
      case 'COMPANY': return 'blue';
      case 'USER': return 'green';
      case 'SUBSCRIPTION': return 'purple';
      case 'INVOICE': return 'orange';
      case 'PLAN': return 'cyan';
      default: return 'default';
    }
  };

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date) => new Date(date).toLocaleString()
    },
    {
      title: 'Admin',
      key: 'admin',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.adminName || 'Unknown'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.adminEmail}</Text>
        </Space>
      )
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 200,
      render: (action) => (
        <Tag color={getActionColor(action)}>
          {action.replace(/_/g, ' ')}
        </Tag>
      )
    },
    {
      title: 'Target',
      key: 'target',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={getTargetTypeColor(record.targetType)}>{record.targetType}</Tag>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.targetId.substring(0, 8)}...
          </Text>
        </Space>
      )
    },
    {
      title: 'IP Address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 130,
      render: (ip) => ip || '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          title="View Details"
        />
      )
    }
  ];

  // Mobile card configuration
  const mobileCardConfig = {
    primaryField: 'action' as const,
    secondaryField: 'adminEmail' as const,
    statusField: 'targetType' as const,
    statusConfig: {
      COMPANY: { color: 'blue', text: 'Company' },
      USER: { color: 'green', text: 'User' },
      SUBSCRIPTION: { color: 'purple', text: 'Subscription' },
      INVOICE: { color: 'orange', text: 'Invoice' },
      PLAN: { color: 'cyan', text: 'Plan' },
    },
    avatar: {
      icon: <HistoryOutlined />,
    },
    fields: [
      {
        key: 'action' as const,
        label: 'Action',
        render: (v: string) => (
          <Tag color={getActionColor(v)}>{v.replace(/_/g, ' ')}</Tag>
        ),
      },
      {
        key: 'createdAt' as const,
        label: 'Time',
        render: (v: string) => new Date(v).toLocaleString(),
      },
      {
        key: 'ipAddress' as const,
        label: 'IP',
        render: (v: string) => v || '-',
      },
    ],
    onClick: handleViewDetails,
  };

  // Mobile filters
  const mobileFilters = [
    {
      key: 'action',
      placeholder: 'Action Type',
      options: actionTypes.map(a => ({ label: a.replace(/_/g, ' '), value: a })),
      value: filterAction,
      onChange: (v: string) => {
        setFilterAction(v);
        fetchLogs({ page: 1 });
      },
    },
    {
      key: 'targetType',
      placeholder: 'Target Type',
      options: [
        { label: 'Company', value: 'COMPANY' },
        { label: 'User', value: 'USER' },
        { label: 'Subscription', value: 'SUBSCRIPTION' },
        { label: 'Invoice', value: 'INVOICE' },
        { label: 'Plan', value: 'PLAN' },
      ],
      value: filterTargetType,
      onChange: (v: string) => {
        setFilterTargetType(v);
        fetchLogs({ page: 1 });
      },
    },
  ];

  // Mobile view
  if (isMobile) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <HistoryOutlined /> Audit Logs
          </Title>
        </div>

        <MobileDataList
          data={logs}
          keyField="id"
          cardConfig={mobileCardConfig}
          loading={loading}
          emptyText="No audit logs found"
          onRefresh={() => fetchLogs()}
          filters={mobileFilters}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page, pageSize) => fetchLogs({ page, limit: pageSize }),
          }}
          swipeActions={{
            right: {
              icon: <EyeOutlined />,
              color: '#1890ff',
              label: 'View',
              onClick: handleViewDetails,
            },
          }}
        />

        {/* Details Drawer (Mobile - bottom sheet) */}
        <Drawer
          title="Audit Log Details"
          placement="bottom"
          height="90%"
          open={drawerVisible}
          onClose={() => setDrawerVisible(false)}
        >
          {selectedLog && (
            <div>
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Card size="small" title="Log Info">
                  <div style={{ display: 'grid', gap: 12 }}>
                    <div>
                      <Text type="secondary">Time</Text>
                      <div><Text>{new Date(selectedLog.createdAt).toLocaleString()}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Admin</Text>
                      <div><Text strong>{selectedLog.adminName || selectedLog.adminEmail}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Admin Email</Text>
                      <div><Text>{selectedLog.adminEmail}</Text></div>
                    </div>
                    <div>
                      <Text type="secondary">Action</Text>
                      <div>
                        <Tag color={getActionColor(selectedLog.action)}>
                          {selectedLog.action.replace(/_/g, ' ')}
                        </Tag>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary">Target Type</Text>
                      <div>
                        <Tag color={getTargetTypeColor(selectedLog.targetType)}>
                          {selectedLog.targetType}
                        </Tag>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary">Target ID</Text>
                      <div>
                        <Text copyable style={{ fontSize: 12, wordBreak: 'break-all' }}>
                          {selectedLog.targetId}
                        </Text>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary">IP Address</Text>
                      <div><Text>{selectedLog.ipAddress || '-'}</Text></div>
                    </div>
                    {selectedLog.userAgent && (
                      <div>
                        <Text type="secondary">User Agent</Text>
                        <div>
                          <Text style={{ fontSize: 11, wordBreak: 'break-all' }}>
                            {selectedLog.userAgent}
                          </Text>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>

                {selectedLog.details && (
                  <Card size="small" title="Details">
                    <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: 0, overflow: 'auto' }}>
                      {JSON.stringify(selectedLog.details, null, 2)}
                    </pre>
                  </Card>
                )}

                {selectedLog.targetDetails && (
                  <Card size="small" title="Target Details">
                    {selectedLog.targetDetails.deleted ? (
                      <Text type="secondary">Target has been deleted</Text>
                    ) : (
                      <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: 0, overflow: 'auto' }}>
                        {JSON.stringify(selectedLog.targetDetails, null, 2)}
                      </pre>
                    )}
                  </Card>
                )}
              </Space>
            </div>
          )}
        </Drawer>
      </div>
    );
  }

  // Desktop view
  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <HistoryOutlined /> Audit Logs
          </Title>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Action Type"
              style={{ width: 180 }}
              allowClear
              value={filterAction}
              onChange={(v) => {
                setFilterAction(v);
                fetchLogs({ page: 1 });
              }}
            >
              {actionTypes.map(a => (
                <Option key={a} value={a}>{a.replace(/_/g, ' ')}</Option>
              ))}
            </Select>
            <Select
              placeholder="Target Type"
              style={{ width: 140 }}
              allowClear
              value={filterTargetType}
              onChange={(v) => {
                setFilterTargetType(v);
                fetchLogs({ page: 1 });
              }}
            >
              <Option value="COMPANY">Company</Option>
              <Option value="USER">User</Option>
              <Option value="SUBSCRIPTION">Subscription</Option>
              <Option value="INVOICE">Invoice</Option>
              <Option value="PLAN">Plan</Option>
            </Select>
            <RangePicker
              onChange={(dates) => {
                setDateRange(dates as any);
                fetchLogs({ page: 1 });
              }}
            />
            <Button icon={<ReloadOutlined />} onClick={() => fetchLogs()}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} logs`
          }}
          onChange={handleTableChange}
          size="small"
        />
      </Card>

      {/* Details Drawer */}
      <Drawer
        title="Audit Log Details"
        placement="right"
        width={600}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedLog && (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Time">
                {new Date(selectedLog.createdAt).toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label="Admin">
                {selectedLog.adminName || selectedLog.adminEmail}
              </Descriptions.Item>
              <Descriptions.Item label="Admin Email">
                {selectedLog.adminEmail}
              </Descriptions.Item>
              <Descriptions.Item label="Action">
                <Tag color={getActionColor(selectedLog.action)}>
                  {selectedLog.action.replace(/_/g, ' ')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Target Type">
                <Tag color={getTargetTypeColor(selectedLog.targetType)}>
                  {selectedLog.targetType}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Target ID">
                <Text copyable>{selectedLog.targetId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="IP Address">
                {selectedLog.ipAddress || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="User Agent">
                <Text style={{ fontSize: 12, wordBreak: 'break-all' }}>
                  {selectedLog.userAgent || '-'}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {selectedLog.details && (
              <>
                <Title level={5} style={{ marginTop: 24 }}>Details</Title>
                <Card size="small">
                  <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: 0 }}>
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </Card>
              </>
            )}

            {selectedLog.targetDetails && (
              <>
                <Title level={5} style={{ marginTop: 24 }}>Target Details</Title>
                <Card size="small">
                  {selectedLog.targetDetails.deleted ? (
                    <Text type="secondary">Target has been deleted</Text>
                  ) : (
                    <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap', margin: 0 }}>
                      {JSON.stringify(selectedLog.targetDetails, null, 2)}
                    </pre>
                  )}
                </Card>
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLogs;
